"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowRight, CheckCircle2, ClipboardList, GraduationCap, Paperclip, UserPlus, type LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PROJECT_HOME_INTENT_FIELD_ID } from "@/components/project-home/ProjectHomeIntentField";
import { buildNextStepDeepLink } from "@/lib/entityDeepLinks";
import { getConversationRowMeta, sessionStatusBadgeClass } from "@/lib/conversationRowMeta";
import { useConversationRunning } from "@/hooks/useConversationRunning";
import { formatDecisionRowDisambiguator } from "@/lib/projectHomePathRows";
import { buildWorkbenchConversationPath } from "@/lib/workbenchArtifactTabUrl";
import { cn } from "@/lib/utils";
import type { CNSConversation, CNSProjectNextStep, CNSProjectNextStepKind } from "@/models/cns";
export function formatAge(ageDays: number): string {
  if (ageDays <= 0) return "today";
  if (ageDays === 1) return "yesterday";
  if (ageDays < 7) return `${ageDays}d ago`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w ago`;
  if (ageDays < 365) return `${Math.floor(ageDays / 30)}mo ago`;
  return `${Math.floor(ageDays / 365)}y ago`;
}
export function formatRelative(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInHours / 24;
  if (diffInHours < 1) return "Just now";
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  if (diffInDays < 2) return "Yesterday";
  if (diffInDays < 7) {
    return date.toLocaleDateString("en-US", {
      weekday: "short"
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}
function formatDecisionUpdatedAt(step: CNSProjectNextStep, conversation: CNSConversation | null): string {
  const dateSource = conversation?.updatedAt ?? conversation?.createdAt;
  if (dateSource) return formatRelative(dateSource);
  return `updated ${formatAge(step.ageDays)}`;
}
function kindIcon(kind: CNSProjectNextStepKind): LucideIcon {
  switch (kind) {
    case "CONVERSATION_PENDING_ACTION":
      return Paperclip;
    case "HYPOTHESIS_FINALIZE":
    case "HYPOTHESIS_NEEDS_EXPERIMENT":
    case "EXPERIMENT_REVIEW":
      return CheckCircle2;
    case "EXPERIMENT_ASSIGN":
      return UserPlus;
    case "EXPERIMENT_CAPTURE_RESULTS":
    case "EXPERIMENT_LOCK_TRACKER":
      return ClipboardList;
    case "LEARNING_VALIDATE":
      return GraduationCap;
    default:
      return ArrowRight;
  }
}
export function focusProjectHomeIntentField() {
  const el = document.getElementById(PROJECT_HOME_INTENT_FIELD_ID);
  if (el instanceof HTMLElement) {
    el.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    el.querySelector("textarea")?.focus();
  }
}
export function DecisionRow(t0) {
  const $ = _c(58);
  const {
    step,
    conversation,
    duplicateTitles,
    decisionPathIndexes,
    decisionSteps,
    conversationById,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== step) {
    t2 = buildNextStepDeepLink(step);
    $[0] = step;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const href = t2;
  let t3;
  if ($[2] !== conversation || $[3] !== step) {
    t3 = formatDecisionUpdatedAt(step, conversation);
    $[2] = conversation;
    $[3] = step;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const updatedText = t3;
  const assigneeText = step.assigneeUserPublicId ? updatedText ? `Waiting since ${updatedText}` : "Assigned" : "Unassigned";
  let t4;
  if ($[5] !== step.kind) {
    t4 = kindIcon(step.kind);
    $[5] = step.kind;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const Icon = t4;
  const overdue = step.severity === "OVERDUE";
  let t5;
  if ($[7] !== conversation || $[8] !== conversationById || $[9] !== decisionPathIndexes || $[10] !== decisionSteps || $[11] !== duplicateTitles || $[12] !== step) {
    t5 = formatDecisionRowDisambiguator(step, conversation, {
      duplicateTitles,
      pathIndexes: decisionPathIndexes,
      decisionSteps,
      conversationById
    });
    $[7] = conversation;
    $[8] = conversationById;
    $[9] = decisionPathIndexes;
    $[10] = decisionSteps;
    $[11] = duplicateTitles;
    $[12] = step;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const disambiguator = t5;
  let t6;
  if ($[14] !== conversation || $[15] !== disambiguator || $[16] !== step.entityTitle) {
    t6 = !disambiguator && conversation && conversation.title.trim() !== step.entityTitle.trim() ? conversation.title.trim() : null;
    $[14] = conversation;
    $[15] = disambiguator;
    $[16] = step.entityTitle;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const conversationSubtitle = t6;
  const t7 = compact ? "py-2.5" : "py-3.5";
  const t8 = overdue && "border-l-2 border-l-destructive pl-3";
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = cn("group flex items-start gap-3 transition-colors", t7, "hover:bg-muted/40 focus-visible:bg-muted/40", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", t8);
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = step.stepId;
  const t11 = step.kind;
  const t12 = step.severity;
  let t13;
  if ($[21] !== Icon || $[22] !== compact || $[23] !== overdue || $[24] !== step.primaryActionLabel) {
    t13 = !compact ? <span className={cn("inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-0.5 text-sm font-medium", overdue ? "border-destructive/25 bg-destructive/10 text-destructive" : "border-border bg-muted text-foreground group-hover:bg-muted/80")}><Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden={true} /><span className="truncate">{step.primaryActionLabel}</span></span> : null;
    $[21] = Icon;
    $[22] = compact;
    $[23] = overdue;
    $[24] = step.primaryActionLabel;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = compact ? "text-sm" : "text-base";
  let t15;
  if ($[26] !== t14) {
    t15 = cn("truncate font-semibold tracking-tight text-foreground", t14);
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== step.entityTitle || $[29] !== t15) {
    t16 = <p className={t15}>{step.entityTitle}</p>;
    $[28] = step.entityTitle;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== conversationSubtitle || $[32] !== disambiguator) {
    t17 = disambiguator ? <p className="truncate text-xs text-muted-foreground">{disambiguator}</p> : conversationSubtitle ? <p className="truncate text-xs text-muted-foreground">{conversationSubtitle}</p> : null;
    $[31] = conversationSubtitle;
    $[32] = disambiguator;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== assigneeText || $[35] !== compact || $[36] !== step.primaryActionLabel || $[37] !== updatedText) {
    t18 = compact ? [step.primaryActionLabel, updatedText].filter(Boolean).join(" \xB7 ") : assigneeText;
    $[34] = assigneeText;
    $[35] = compact;
    $[36] = step.primaryActionLabel;
    $[37] = updatedText;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18) {
    t19 = <p className="text-xs text-muted-foreground">{t18}</p>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t13 || $[42] !== t16 || $[43] !== t17 || $[44] !== t19) {
    t20 = <div className="min-w-0 flex-1 space-y-1.5">{t13}{t16}{t17}{t19}</div>;
    $[41] = t13;
    $[42] = t16;
    $[43] = t17;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  const t21 = overdue ? "text-destructive/50 group-hover:text-destructive" : "text-muted-foreground/50 group-hover:text-foreground";
  let t22;
  if ($[46] !== t21) {
    t22 = cn("mt-1 size-4 shrink-0 transition-transform group-hover:translate-x-0.5", t21);
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t22) {
    t23 = <ArrowRight className={t22} aria-hidden={true} />;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== href || $[51] !== step.kind || $[52] !== step.severity || $[53] !== step.stepId || $[54] !== t20 || $[55] !== t23 || $[56] !== t9) {
    t24 = <li><Link href={href} className={t9} data-step-id={t10} data-step-kind={t11} data-step-severity={t12}>{t20}{t23}</Link></li>;
    $[50] = href;
    $[51] = step.kind;
    $[52] = step.severity;
    $[53] = step.stepId;
    $[54] = t20;
    $[55] = t23;
    $[56] = t9;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  return t24;
}
export function PassiveRow(t0) {
  const $ = _c(69);
  const {
    conversation,
    projectId,
    depth,
    childCount,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const isRunning = useConversationRunning(conversation.id);
  let t2;
  if ($[0] !== conversation.id || $[1] !== projectId) {
    t2 = buildWorkbenchConversationPath(projectId, conversation.id, "overview");
    $[0] = conversation.id;
    $[1] = projectId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const href = t2;
  const dateSource = conversation.updatedAt ?? conversation.createdAt;
  let T0;
  let T1;
  let branchNote;
  let detailLine;
  let rowMeta;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== childCount || $[4] !== compact || $[5] !== conversation || $[6] !== depth || $[7] !== href || $[8] !== isRunning) {
    rowMeta = getConversationRowMeta({
      ...conversation,
      attentionHint: null
    }, {
      isRunningLocally: isRunning,
      hasNextStep: false
    });
    branchNote = conversation.parentConversationPublicId ? "Branched conversation" : childCount > 0 ? `${childCount} separate conversation${childCount === 1 ? "" : "s"}` : null;
    detailLine = rowMeta.secondaryLine;
    if ($[22] !== depth) {
      t10 = depth > 0 ? {
        paddingLeft: `${depth}rem`
      } : undefined;
      $[22] = depth;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    T1 = Link;
    t8 = href;
    const t11 = compact ? "py-2" : "py-3";
    if ($[24] !== t11) {
      t9 = cn("group flex items-start gap-3 transition-colors", t11, "hover:bg-muted/40 focus-visible:bg-muted/40", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50");
      $[24] = t11;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t7 = "min-w-0 flex-1 space-y-0.5";
    t5 = "flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1";
    const t12 = compact ? "text-sm" : "text-sm";
    let t13;
    if ($[26] !== t12) {
      t13 = cn("min-w-0 truncate font-medium text-foreground", t12);
      $[26] = t12;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    if ($[28] !== conversation.title || $[29] !== t13) {
      t6 = <p className={t13}>{conversation.title}</p>;
      $[28] = conversation.title;
      $[29] = t13;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    T0 = Badge;
    t3 = "secondary";
    t4 = cn("shrink-0 px-2 py-0 text-xs font-medium", sessionStatusBadgeClass(rowMeta.sessionStatus));
    $[3] = childCount;
    $[4] = compact;
    $[5] = conversation;
    $[6] = depth;
    $[7] = href;
    $[8] = isRunning;
    $[9] = T0;
    $[10] = T1;
    $[11] = branchNote;
    $[12] = detailLine;
    $[13] = rowMeta;
    $[14] = t10;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    branchNote = $[11];
    detailLine = $[12];
    rowMeta = $[13];
    t10 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[31] !== T0 || $[32] !== rowMeta.sessionStatusLabel || $[33] !== t3 || $[34] !== t4) {
    t11 = <T0 variant={t3} className={t4}>{rowMeta.sessionStatusLabel}</T0>;
    $[31] = T0;
    $[32] = rowMeta.sessionStatusLabel;
    $[33] = t3;
    $[34] = t4;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== rowMeta.secondaryLine || $[37] !== rowMeta.stageLabel) {
    t12 = rowMeta.stageLabel && rowMeta.secondaryLine !== rowMeta.stageLabel ? <Badge variant="outline" className="shrink-0 px-2 py-0 text-xs font-medium">{rowMeta.stageLabel}</Badge> : null;
    $[36] = rowMeta.secondaryLine;
    $[37] = rowMeta.stageLabel;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t5 || $[42] !== t6) {
    t13 = <div className={t5}>{t6}{t11}{t12}</div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t5;
    $[42] = t6;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== dateSource) {
    t14 = formatRelative(dateSource);
    $[44] = dateSource;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== branchNote || $[47] !== t14) {
    t15 = [t14, branchNote].filter(Boolean);
    $[46] = branchNote;
    $[47] = t14;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const t16 = t15.join(" \xB7 ");
  let t17;
  if ($[49] !== t16) {
    t17 = <p className="text-xs text-muted-foreground">{t16}</p>;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== compact || $[52] !== detailLine || $[53] !== rowMeta.secondaryLineFull) {
    t18 = !compact && detailLine ? <p className="truncate text-xs text-muted-foreground/80" title={rowMeta.secondaryLineFull ?? detailLine}>{detailLine}</p> : null;
    $[51] = compact;
    $[52] = detailLine;
    $[53] = rowMeta.secondaryLineFull;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t13 || $[56] !== t17 || $[57] !== t18 || $[58] !== t7) {
    t19 = <div className={t7}>{t13}{t17}{t18}</div>;
    $[55] = t13;
    $[56] = t17;
    $[57] = t18;
    $[58] = t7;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" aria-hidden={true} />;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== T1 || $[62] !== t19 || $[63] !== t8 || $[64] !== t9) {
    t21 = <T1 href={t8} className={t9}>{t19}{t20}</T1>;
    $[61] = T1;
    $[62] = t19;
    $[63] = t8;
    $[64] = t9;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== t10 || $[67] !== t21) {
    t22 = <li style={t10}>{t21}</li>;
    $[66] = t10;
    $[67] = t21;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  return t22;
}
export function ProjectHomeListSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul role="list" className="divide-y divide-border/50" aria-hidden={true}>{[0, 1, 2].map(_temp)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(i) {
  return <li key={i} className="space-y-2 py-3.5"><div className="h-4 w-1/2 rounded bg-muted" /><div className="h-6 w-2/3 rounded-md bg-muted/70" /><div className="h-2 w-1/3 rounded bg-muted/50" /></li>;
}